import { useCallback, useEffect, useState } from "react";
import { BarChart3, Loader2, Calendar, Users2, TrendingUp, TrendingDown, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Forbidden from "@/pages/Forbidden";

const ALLOWED_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant", "manager", "account_manager", "hr"]);
const FINANCE_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant"]);
const inputCls = "h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";
const fmtMoney = (v) => { try { return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(v || 0); } catch { return `₺${v || 0}`; } };
const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function PersonnelReport() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/personnel", { params: { user_id: "__all__" } });
            setRows(data.items || []);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>;

    // Only show rows with any activity for cleanliness
    const activeRows = rows.filter((r) => r.total_tasks > 0 || r.active_projects > 0 || (r.expenses && r.expenses.count > 0));
    return (
        <div className="space-y-4">
            <div className="card-elevated overflow-hidden">
                <table className="w-full min-w-[900px]">
                    <thead><tr className="border-b border-border bg-muted/40">
                        {["Personel", "Departman", "Görev (Tamam/Toplam)", "Tamamlanma", "Devam Eden", "Gecikmiş", "Aktif Proje", "Gider"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-[11.5px] font-semibold uppercase text-muted-foreground">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {activeRows.length === 0 ? <tr><td colSpan="8" className="px-4 py-12 text-center text-muted-foreground">Aktif kayıt yok.</td></tr>
                        : activeRows.map((r) => (
                            <tr key={r.user_id} className="border-b border-border last:border-b-0 hover:bg-muted/40" data-testid={`personnel-row-${r.user_id}`}>
                                <td className="px-4 py-3 text-[13px] font-semibold">{r.full_name || "—"}<div className="text-[11px] text-muted-foreground font-normal">{r.title || r.role || ""}</div></td>
                                <td className="px-4 py-3 text-[12.5px] text-muted-foreground">{r.department || "—"}</td>
                                <td className="px-4 py-3 text-[13px] font-bold tabular-nums">{r.done_tasks} / {r.total_tasks}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                                            <div className="h-full bg-[hsl(var(--brand))]" style={{ width: `${Math.min(100, r.completion_rate)}%` }} />
                                        </div>
                                        <span className="text-[12px] font-bold tabular-nums">%{r.completion_rate}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-[12.5px]">{r.in_progress_tasks}</td>
                                <td className="px-4 py-3 text-[12.5px]">{r.overdue_tasks > 0 ? <span className="text-[hsl(var(--destructive))] font-semibold">{r.overdue_tasks}</span> : r.overdue_tasks}</td>
                                <td className="px-4 py-3 text-[12.5px]">{r.active_projects}</td>
                                <td className="px-4 py-3 text-[12.5px] font-semibold">{r.expenses ? fmtMoney(r.expenses.total) : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function MonthlyReport() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/monthly", { params: { year, month } });
            setData(data);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [year, month]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="card-elevated p-3 flex flex-wrap gap-2 items-center">
                <div className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground px-1"><Filter className="h-3.5 w-3.5" /> Dönem</div>
                <select className={inputCls} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="month-select">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <select className={inputCls} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>
            {loading ? <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>
            : !data ? null
            : (
                <>
                    {data.total && (
                        <div className="card-elevated p-4">
                            <div className="text-[13px] font-bold mb-3">{MONTHS[month - 1]} {year} — Genel Toplam (Ajans)</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                {data.include_finance && <>
                                    <div><div className="text-[10.5px] uppercase text-muted-foreground">Gelir</div><div className="text-[16px] font-bold tabular-nums text-[hsl(var(--success))]">{fmtMoney(data.total.revenue)}</div></div>
                                    <div><div className="text-[10.5px] uppercase text-muted-foreground">Gider</div><div className="text-[16px] font-bold tabular-nums text-[hsl(var(--destructive))]">{fmtMoney(data.total.expenses)}</div></div>
                                    <div><div className="text-[10.5px] uppercase text-muted-foreground">Kâr</div><div className="text-[16px] font-bold tabular-nums">{fmtMoney(data.total.profit)}</div></div>
                                </>}
                                <div><div className="text-[10.5px] uppercase text-muted-foreground">Aktif Proje</div><div className="text-[16px] font-bold tabular-nums">{data.total.active_projects}</div></div>
                                <div><div className="text-[10.5px] uppercase text-muted-foreground">Yayınlanan İçerik</div><div className="text-[16px] font-bold tabular-nums">{data.total.published_content}</div></div>
                                <div><div className="text-[10.5px] uppercase text-muted-foreground">Açık Teklif</div><div className="text-[16px] font-bold tabular-nums">{data.total.open_proposals}</div></div>
                            </div>
                        </div>
                    )}

                    <div className="card-elevated overflow-hidden">
                        <table className="w-full min-w-[900px]">
                            <thead><tr className="border-b border-border bg-muted/40">
                                {["Müşteri", data.include_finance && "Gelir", data.include_finance && "Bekleyen", "Aktif Proje", "Tamamlanan", "Yayınlanan İçerik", "Açık Teklif", "Aktif Sözleşme"].filter(Boolean).map((h) => (
                                    <th key={h} className="px-4 py-3 text-left text-[11.5px] font-semibold uppercase text-muted-foreground">{h}</th>
                                ))}
                            </tr></thead>
                            <tbody>
                                {data.items.length === 0 ? <tr><td colSpan={data.include_finance ? 8 : 6} className="px-4 py-12 text-center text-muted-foreground">Bu ay için müşteri bazlı aktivite yok.</td></tr>
                                : data.items.map((r) => (
                                    <tr key={r.customer_id} className="border-b border-border last:border-b-0 hover:bg-muted/40" data-testid={`monthly-row-${r.customer_id}`}>
                                        <td className="px-4 py-3 text-[13px] font-semibold">{r.customer_name}</td>
                                        {data.include_finance && <td className="px-4 py-3 text-[13px] font-bold tabular-nums text-[hsl(var(--success))]">{fmtMoney(r.revenue)}</td>}
                                        {data.include_finance && <td className="px-4 py-3 text-[12.5px] tabular-nums text-muted-foreground">{fmtMoney(r.outstanding)}</td>}
                                        <td className="px-4 py-3 text-[12.5px]">{r.active_projects}</td>
                                        <td className="px-4 py-3 text-[12.5px]">{r.completed_projects}</td>
                                        <td className="px-4 py-3 text-[12.5px]">{r.published_content}</td>
                                        <td className="px-4 py-3 text-[12.5px]">{r.open_proposals}</td>
                                        <td className="px-4 py-3 text-[12.5px]">{r.active_contracts}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default function ReportsPage() {
    const { user } = useAuth();
    if (!user) return null;
    if (!ALLOWED_ROLES.has(user.role)) return <Forbidden />;
    return (
        <div className="space-y-4 animate-fade-in">
            <div>
                <h1 className="text-[26px] font-bold tracking-tight flex items-center gap-2" data-testid="reports-title"><BarChart3 className="h-6 w-6 text-[hsl(var(--brand))]" /> Raporlar</h1>
                <p className="text-[13.5px] text-muted-foreground mt-1">Personel performansı ve aylık firma bazlı raporlar.</p>
            </div>
            <Tabs defaultValue="personnel" className="space-y-4">
                <TabsList className="bg-muted/50 p-1 rounded-xl">
                    <TabsTrigger value="personnel" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-card" data-testid="reports-tab-personnel"><Users2 className="h-3.5 w-3.5 mr-1" /> Personel Raporu</TabsTrigger>
                    <TabsTrigger value="monthly" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-card" data-testid="reports-tab-monthly"><Calendar className="h-3.5 w-3.5 mr-1" /> Aylık Firma Raporu</TabsTrigger>
                </TabsList>
                <TabsContent value="personnel"><PersonnelReport /></TabsContent>
                <TabsContent value="monthly"><MonthlyReport /></TabsContent>
            </Tabs>
        </div>
    );
}
